import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ChevronRight, Users, Heart, Globe, BookOpen, Smile, Baby, Coffee, Instagram, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/app/language/useLanguage";
import Seo from "@/app/seo/Seo";
import { routeMetadata } from "@/app/seo/route-metadata";
import { useContentStatus } from "@/app/use-content-status";
import { getHomePageContent, selectHomeUpcomingEvents, type EventData, type HomepageData, type SeriesData } from "@/services/sanity/content";
import { urlFor } from "@/services/sanity/image";
import logo from "@/assets/logo.svg";
import RegularGatheringFallbacks from "@/components/events/RegularGatheringFallbacks";
import { EventCard } from "@/components/events/EventCard";
import { EventModal } from "@/components/events/EventModal";
import {
    fetchInstagramPhotos,
    getFallbackInstagramItems,
    type InstagramMediaItem,
    INSTAGRAM_HANDLE,
    INSTAGRAM_PROFILE_URL,
} from "@/services/instagram/instagram";

const BENTO_CLASSES = [
    "col-span-2 md:col-span-2 md:row-span-2", // Photo 1: Large landscape top left
    "col-span-1 md:col-span-1 md:row-span-2", // Photo 2: Portrait middle
    "col-span-1 md:col-span-1 md:row-span-1", // Photo 3: Small square top right
    "col-span-1 md:col-span-1 md:row-span-1", // Photo 4: Small square bottom right
    "col-span-1 md:col-span-2 md:row-span-1", // Photo 5: Landscape bottom left
    "col-span-2 md:col-span-2 md:row-span-1", // Photo 6: Landscape bottom right
] as const;

const Home: React.FC = () => {
    const [homeData, setHomeData] = useState<HomepageData | null>(null);
    const [seriesData, setSeriesData] = useState<SeriesData | null>(null);
    const [eventsData, setEventsData] = useState<EventData[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<EventData | null>(null);
    const [instagramPhotos, setInstagramPhotos] = useState<InstagramMediaItem[]>(() =>
        getFallbackInstagramItems().slice(0, 6)
    );
    const { clearStatus, reportStatus } = useContentStatus();
    const { t } = useTranslation();
    const { language } = useLanguage();

    const upcomingEvents = useMemo(() => selectHomeUpcomingEvents(eventsData, 3), [eventsData]);
    const kidsCheckinUrl = import.meta.env.VITE_KIDS_CHECKIN_URL || "https://kids.aucklandchurch.com";

    useEffect(() => {
        let cancelled = false;

        const loadHomeContent = async () => {
            const result = await getHomePageContent();

            if (cancelled) {
                return;
            }

            setHomeData(result.data.homepage);
            setSeriesData(result.data.series);
            setEventsData(result.data.events);

            if (result.source === "fallback") {
                reportStatus("home", result.source, result.error || "Live homepage content is unavailable right now. Showing saved homepage content instead.");
                return;
            }

            clearStatus("home");
        };

        void loadHomeContent();

        return () => {
            cancelled = true;
            clearStatus("home");
        };
    }, [clearStatus, reportStatus]);

    useEffect(() => {
        let cancelled = false;

        const loadInstagram = async () => {
            try {
                const res = await fetchInstagramPhotos();
                if (!cancelled && res.items && res.items.length >= 6) {
                    setInstagramPhotos(res.items.slice(0, 6));
                }
            } catch {
                // Silently maintain fallbacks
            }
        };

        void loadInstagram();
        return () => {
            cancelled = true;
        };
    }, []);

    // Helper to render expectation icons
    const renderExpectationIcon = (iconName: string) => {
        switch (iconName) {
            case "smile":
                return <Smile className="w-6 h-6 text-primary" aria-hidden="true" />;
            case "baby":
                return <Baby className="w-6 h-6 text-primary" aria-hidden="true" />;
            case "coffee":
                return <Coffee className="w-6 h-6 text-primary" aria-hidden="true" />;
            default:
                return <Smile className="w-6 h-6 text-primary" aria-hidden="true" />;
        }
    };

    return (
        <div className="flex flex-col w-full bg-transparent text-secondary dark:text-gray-100 transition-colors duration-300">
            <Seo {...routeMetadata.home} />

            {/* Hero Intro */}
            <section className="relative pt-12 pb-16 md:pt-20 md:pb-20">
                {/* Gentle ambient background warmth */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(36,110,44,0.12),transparent_70%)] dark:bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,rgba(36,110,44,0.20),transparent_70%)]"
                />

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Editorial Header */}
                    <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-primary/10 text-primary dark:bg-primary/20 text-xs font-bold tracking-wider uppercase mb-6">
                            <span>Central Auckland Church of Christ</span>
                        </div>

                        <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-secondary dark:text-white leading-[1.1] mb-6">
                            {language === "mi"
                                ? homeData?.heroHeadingMi || t("home.heroHeading")
                                : homeData?.heroHeading || (
                                      <>
                                          Welcome to <span className="text-primary">Auckland Church</span>
                                      </>
                                  )}
                        </h1>

                        <p className="text-lg sm:text-xl text-stone-600 dark:text-stone-300 leading-relaxed font-light mb-8 max-w-2xl mx-auto">
                            {language === "mi"
                                ? homeData?.heroSubheadingMi || t("home.heroSubheading")
                                : homeData?.heroSubheading || t("home.heroSubheading")}
                        </p>

                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <Link
                                to="/visit"
                                className="inline-flex items-center justify-center px-7 py-3.5 text-base font-semibold rounded-full text-white bg-primary hover:bg-primary-hover shadow-sm hover:shadow transition-all duration-200"
                            >
                                {t("home.planYourVisit")}
                                <ChevronRight className="ml-2 h-4 w-4" aria-hidden="true" />
                            </Link>
                            <a
                                href="#what-to-expect"
                                className="inline-flex items-center justify-center px-7 py-3.5 text-base font-medium rounded-full text-stone-700 dark:text-stone-200 bg-white dark:bg-warm-dark-surface border border-warm-border dark:border-warm-dark-border hover:border-primary/50 transition-all duration-200"
                            >
                                {t("home.learnMore")}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Full-Width Parallax Hero Image */}
            <section className="w-full h-[240px] sm:h-[320px] md:h-[50vh] md:min-h-[400px] bg-scroll md:bg-fixed bg-center bg-cover relative border-b border-warm-border dark:border-warm-dark-border bg-hero-parallax">
                {/* Dark overlay for contrast */}
                <div className="absolute inset-0 bg-black/30 dark:bg-black/50 mix-blend-multiply" aria-hidden="true" />
            </section>

            {/* Grounded Sunday Gathering Snapshot Card */}
            <section className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 md:-mt-32 pb-16 md:pb-24">
                <div className="bg-white dark:bg-warm-dark-surface rounded-2xl border border-warm-border dark:border-warm-dark-border shadow-md p-6 sm:p-8 lg:p-10 transition-colors">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8 items-center">
                            {/* Service Details */}
                            <div className="md:col-span-7 space-y-4">
                                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                                    <Clock className="w-4 h-4" aria-hidden="true" />
                                    <span>{t("home.sundayService")}</span>
                                </div>
                                <h2 className="font-serif text-2xl sm:text-3xl font-bold text-secondary dark:text-white">
                                    Join Us This Sunday in Freemans Bay
                                </h2>
                                <p className="text-stone-600 dark:text-stone-300 text-base leading-relaxed">
                                    {t("home.serviceTimesNote")}
                                </p>
                                <div className="flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-stone-500 dark:text-stone-400 pt-1">
                                    <span className="flex items-center gap-1.5">
                                        <Smile className="w-4 h-4 text-primary" aria-hidden="true" />
                                        Casual dress
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Baby className="w-4 h-4 text-primary" aria-hidden="true" />
                                        Kids classes
                                    </span>
                                    <span className="flex items-center gap-1.5">
                                        <Coffee className="w-4 h-4 text-primary" aria-hidden="true" />
                                        Tea & coffee after
                                    </span>
                                </div>
                            </div>

                            {/* Location & Directions */}
                            <div className="md:col-span-5 bg-warm-stone/50 dark:bg-warm-dark-bg/60 p-6 rounded-xl border border-warm-border/80 dark:border-warm-dark-border/80 flex flex-col justify-between gap-4">
                                <div>
                                    <div className="flex items-start gap-3">
                                        <MapPin className="w-5 h-5 text-primary mt-1 shrink-0" aria-hidden="true" />
                                        <div>
                                            <p className="font-bold text-secondary dark:text-white text-base">
                                                {t("visit.venueName")}
                                            </p>
                                            <p className="text-stone-600 dark:text-stone-300 text-sm mt-0.5">
                                                52 Hepburn Street, Freemans Bay
                                            </p>
                                            <p className="text-stone-500 dark:text-stone-400 text-xs mt-1">
                                                {t("home.parkingNote")}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 pt-2">
                                    <a
                                        href="https://www.google.com/maps/search/?api=1&query=52+Hepburn+Street+Freemans+Bay+Auckland"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover"
                                    >
                                        Google Maps directions
                                        <ChevronRight className="ml-1 w-4 h-4" aria-hidden="true" />
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
            </section>

            {/* What to Expect (Human, Non-Robotic Walkthrough) */}
            <section id="what-to-expect" className="py-20 md:py-28 bg-transparent border-b border-warm-border/60 dark:border-warm-dark-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="text-xs font-bold uppercase tracking-widest text-primary mb-3 block">
                            {language === "mi" ? "Ngā Tūmanako" : "First Time Visiting"}
                        </span>
                        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-secondary dark:text-white mb-4">
                            {language === "mi"
                                ? homeData?.whatToExpect?.headingMi || t("home.whatToExpectHeading")
                                : homeData?.whatToExpect?.heading || t("home.whatToExpectHeading")}
                        </h2>
                        <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg leading-relaxed">
                            {language === "mi"
                                ? homeData?.whatToExpect?.descriptionMi || t("home.whatToExpectDescription")
                                : homeData?.whatToExpect?.description || t("home.whatToExpectDescription")}
                        </p>
                    </div>

                    {/* Editorial Step Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {homeData?.whatToExpect?.cards ? (
                            homeData.whatToExpect.cards.map((card, idx) => (
                                <div
                                    key={idx}
                                    className="bg-warm-cream/60 dark:bg-warm-dark-bg/60 p-8 rounded-2xl border border-warm-border/80 dark:border-warm-dark-border flex flex-col items-start text-left"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                                        {renderExpectationIcon(card.icon)}
                                    </div>
                                    <span className="text-xs font-mono font-bold text-stone-400 dark:text-stone-400 mb-2">
                                        0{idx + 1}
                                    </span>
                                    <h3 className="font-serif text-xl font-bold text-secondary dark:text-white mb-3">
                                        {language === "mi" ? card.titleMi || card.title : card.title}
                                    </h3>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed">
                                        {language === "mi" ? card.descriptionMi || card.description : card.description}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <>
                                <div className="bg-warm-cream/60 dark:bg-warm-dark-bg/60 p-8 rounded-2xl border border-warm-border/80 dark:border-warm-dark-border flex flex-col items-start text-left">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                                        <Smile className="w-6 h-6 text-primary" aria-hidden="true" />
                                    </div>
                                    <span className="text-xs font-mono font-bold text-stone-400 dark:text-stone-400 mb-2">
                                        01
                                    </span>
                                    <h3 className="font-serif text-xl font-bold text-secondary dark:text-white mb-3">
                                        {t("home.cardAtmosphereTitle")}
                                    </h3>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed">
                                        {t("home.cardAtmosphereBody")}
                                    </p>
                                </div>

                                <div className="bg-warm-cream/60 dark:bg-warm-dark-bg/60 p-8 rounded-2xl border border-warm-border/80 dark:border-warm-dark-border flex flex-col items-start text-left">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                                        <Baby className="w-6 h-6 text-primary" aria-hidden="true" />
                                    </div>
                                    <span className="text-xs font-mono font-bold text-stone-400 dark:text-stone-400 mb-2">
                                        02
                                    </span>
                                    <h3 className="font-serif text-xl font-bold text-secondary dark:text-white mb-3">
                                        {t("home.cardKidsTitle")}
                                    </h3>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed">
                                        {t("home.cardKidsBody")}
                                    </p>
                                </div>

                                <div className="bg-warm-cream/60 dark:bg-warm-dark-bg/60 p-8 rounded-2xl border border-warm-border/80 dark:border-warm-dark-border flex flex-col items-start text-left">
                                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                                        <Coffee className="w-6 h-6 text-primary" aria-hidden="true" />
                                    </div>
                                    <span className="text-xs font-mono font-bold text-stone-400 dark:text-stone-400 mb-2">
                                        03
                                    </span>
                                    <h3 className="font-serif text-xl font-bold text-secondary dark:text-white mb-3">
                                        {t("home.cardCoffeeTitle")}
                                    </h3>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed">
                                        {t("home.cardCoffeeBody")}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Current Teaching Series */}
            <section className="py-20 md:py-24 bg-transparent border-b border-warm-border/60 dark:border-warm-dark-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-white dark:bg-warm-dark-surface rounded-2xl border border-warm-border dark:border-warm-dark-border overflow-hidden shadow-sm">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center p-8 sm:p-12">
                            <div className="lg:col-span-7 space-y-5">
                                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary">
                                    <BookOpen className="w-4 h-4" aria-hidden="true" />
                                    <span>{t("home.currentSeries")}</span>
                                </div>
                                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-secondary dark:text-white">
                                    {seriesData?.title || "The Minor Prophets"}
                                </h2>
                                <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg leading-relaxed font-light">
                                    {seriesData?.description ||
                                        "Join us as we journey through the Minor Prophets, discovering God's heart for justice, mercy, and faithfulness in a broken world."}
                                </p>
                                <div className="pt-2">
                                    <Link
                                        to="/resources"
                                        className="inline-flex items-center justify-center px-6 py-3 rounded-full text-sm font-semibold text-white bg-primary hover:bg-primary-hover transition-colors"
                                    >
                                        <BookOpen className="mr-2 h-4 w-4" />
                                        {t("home.viewResources")}
                                    </Link>
                                </div>
                            </div>

                            <div className="lg:col-span-5 aspect-[4/3] rounded-xl overflow-hidden bg-warm-stone dark:bg-stone-800 relative">
                                {seriesData?.image ? (
                                    <img
                                        src={urlFor(seriesData.image).width(640).url()}
                                        srcSet={`${urlFor(seriesData.image).width(640).url()} 640w, ${urlFor(seriesData.image).width(1280).url()} 1280w`}
                                        sizes="(min-width: 1024px) 40vw, 100vw"
                                        alt={seriesData.title}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center bg-stone-100 dark:bg-stone-800/80 p-8 text-center">
                                        <BookOpen className="w-16 h-16 text-primary/40 mb-3" />
                                        <span className="font-serif text-lg font-bold text-stone-700 dark:text-stone-300">
                                            {seriesData?.title || "Sunday Scripture Study"}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Our Community / Life Together */}
            <section className="py-20 md:py-28 bg-transparent border-b border-warm-border/60 dark:border-warm-dark-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <span className="text-xs font-bold uppercase tracking-widest text-primary mb-3 block">
                            {t("home.ourCommunity")}
                        </span>
                        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-secondary dark:text-white mb-4">
                            {t("home.lifeTogether")}
                        </h2>
                        <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg leading-relaxed">
                            {t("home.lifeTogetherBody")}
                        </p>
                    </div>

                    {/* Asymmetric Community Showcase */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Small Groups Card */}
                        <div className="bg-warm-cream/50 dark:bg-warm-dark-bg/50 p-8 rounded-2xl border border-warm-border dark:border-warm-dark-border flex flex-col justify-between">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                                    <Users className="w-6 h-6" aria-hidden="true" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-secondary dark:text-white mb-3">
                                    {t("home.communityCardTitle")}
                                </h3>
                                <p className="text-stone-600 dark:text-stone-300 text-base leading-relaxed">
                                    {t("home.communityCardBody")}
                                </p>
                            </div>
                            <div className="pt-6">
                                <Link
                                    to="/visit"
                                    className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover"
                                >
                                    Explore small groups
                                    <ChevronRight className="ml-1 w-4 h-4" aria-hidden="true" />
                                </Link>
                            </div>
                        </div>

                        {/* Kids & Youth Card */}
                        <div className="bg-warm-cream/50 dark:bg-warm-dark-bg/50 p-8 rounded-2xl border border-warm-border dark:border-warm-dark-border flex flex-col justify-between">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                                    <Heart className="w-6 h-6" aria-hidden="true" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-secondary dark:text-white mb-3">
                                    {t("home.kidsCardTitle")}
                                </h3>
                                <p className="text-stone-600 dark:text-stone-300 text-base leading-relaxed">
                                    {t("home.kidsCardBody")}
                                </p>
                            </div>
                            <div className="pt-6 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/about"
                                    className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover"
                                >
                                    {t("home.familyMinistries", "Family ministries")}
                                    <ChevronRight className="ml-1 w-4 h-4" aria-hidden="true" />
                                </Link>
                                <a
                                    href={kidsCheckinUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center text-sm font-semibold text-stone-600 hover:text-primary dark:text-stone-300 dark:hover:text-white transition-colors"
                                >
                                    {t("home.kidsCheckinButton", "Kids Check-In Kiosk")}
                                    <ExternalLink className="ml-1.5 w-3.5 h-3.5" aria-hidden="true" />
                                </a>
                            </div>
                        </div>

                        {/* Missions Card */}
                        <div className="bg-warm-cream/50 dark:bg-warm-dark-bg/50 p-8 rounded-2xl border border-warm-border dark:border-warm-dark-border flex flex-col justify-between">
                            <div>
                                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                                    <Globe className="w-6 h-6" aria-hidden="true" />
                                </div>
                                <h3 className="font-serif text-2xl font-bold text-secondary dark:text-white mb-3">
                                    {t("home.missionsCardTitle")}
                                </h3>
                                <p className="text-stone-600 dark:text-stone-300 text-base leading-relaxed">
                                    {t("home.missionsCardBody")}
                                </p>
                            </div>
                            <div className="pt-6">
                                <Link
                                    to="/giving"
                                    className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover"
                                >
                                    Pacific regional missions
                                    <ChevronRight className="ml-1 w-4 h-4" aria-hidden="true" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Community Gallery */}
                    <div className="mt-16 sm:mt-24">
                        <div className="text-center mb-10">
                            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-secondary dark:text-white mb-2">
                                {language === "mi" ? "He Tirohanga Ki Tō Mātou Hapori" : "A Glimpse of Our Community"}
                            </h3>
                            <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base">
                                {language === "mi" ? "Kei te tupu tahi mātou i te whakapono." : "Growing together in faith and fellowship."}
                            </p>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 auto-rows-[160px] sm:auto-rows-[200px] md:auto-rows-[240px]">
                            {instagramPhotos.slice(0, 6).map((photo, idx) => (
                                <Link
                                    to="/gallery"
                                    key={photo.id || idx}
                                    className={`${BENTO_CLASSES[idx] || "col-span-1"} relative rounded-2xl overflow-hidden shadow-sm border border-warm-border/50 dark:border-warm-dark-border group block focus:outline-none focus:ring-2 focus:ring-primary`}
                                    aria-label={`View photo in gallery: ${(photo.caption || "Church moment").slice(0, 50)}...`}
                                >
                                    <img
                                        src={photo.mediaUrl}
                                        alt={photo.altText || photo.caption || "Auckland Church community moment"}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-[1.04]"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none flex items-end p-3 sm:p-4">
                                        <p className="text-white text-xs sm:text-sm line-clamp-2 font-medium drop-shadow-sm">
                                            {photo.caption}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* Gallery Actions */}
                        <div className="mt-8 text-center flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                            <Link
                                to="/gallery"
                                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white bg-secondary hover:bg-secondary/90 transition-all shadow-sm hover:shadow hover:-translate-y-0.5"
                            >
                                <span>{language === "mi" ? "Tirohia ngā whakaahua katoa" : "View Photo Gallery"}</span>
                                <ChevronRight className="w-4 h-4" aria-hidden="true" />
                            </Link>
                            <a
                                href={INSTAGRAM_PROFILE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-zinc-700 dark:text-zinc-200 bg-white dark:bg-zinc-800 border border-warm-border dark:border-warm-dark-border hover:border-primary/60 transition-all shadow-sm hover:shadow hover:-translate-y-0.5"
                                aria-label={`Follow @${INSTAGRAM_HANDLE} on Instagram (opens in a new tab)`}
                            >
                                <Instagram className="w-4 h-4 text-rose-500" aria-hidden="true" />
                                <span>@{INSTAGRAM_HANDLE}</span>
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Upcoming Events */}
            <section className="py-20 md:py-28 bg-transparent border-b border-warm-border/60 dark:border-warm-dark-border">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
                        <div>
                            <span className="text-xs font-bold uppercase tracking-widest text-primary mb-2 block">
                                {t("home.markYourCalendar")}
                            </span>
                            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-secondary dark:text-white">
                                {t("home.upcomingEvents")}
                            </h2>
                        </div>
                        <Link
                            to="/events"
                            className="inline-flex items-center text-sm font-semibold text-primary hover:text-primary-hover"
                        >
                            {t("home.viewAllEvents")}
                            <ChevronRight className="ml-1 w-4 h-4" />
                        </Link>
                    </div>

                    {upcomingEvents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {upcomingEvents.map((event, idx) => (
                                <EventCard
                                    key={idx}
                                    event={event}
                                    t={t}
                                    onSelect={(selected) => setSelectedEvent(selected)}
                                />
                            ))}
                        </div>
                    ) : (
                        <RegularGatheringFallbacks />
                    )}
                </div>
            </section>

            {/* Closing Community Invitation (Replacing 300vh Scroll-jack with Warm Invitation) */}
            <section className="relative overflow-hidden bg-secondary dark:bg-warm-dark-surface text-white py-20 md:py-28">
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_50%_at_50%_100%,rgba(36,110,44,0.30),transparent_70%)]"
                />

                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
                    <img
                        src={logo}
                        alt=""
                        aria-hidden="true"
                        className="w-12 h-12 mx-auto brightness-0 invert opacity-80 mb-4"
                    />

                    <span className="text-xs font-bold uppercase tracking-widest text-primary-soft/80 block">
                        Nau Mai, Haere Mai
                    </span>

                    <h2 className="font-serif text-3xl sm:text-5xl md:text-6xl font-bold tracking-tight">
                        {t("home.ctaTitle")}
                    </h2>

                    <p className="text-base sm:text-xl text-stone-300 max-w-2xl mx-auto font-light leading-relaxed">
                        {t("home.ctaBody")}
                    </p>

                    <div className="flex flex-wrap items-center justify-center gap-4 pt-4">
                        <Link
                            to="/visit"
                            className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-full text-secondary bg-white hover:bg-stone-100 shadow-sm transition-colors"
                        >
                            {t("home.planYourVisit")}
                        </Link>
                        <Link
                            to="/contact"
                            className="inline-flex items-center justify-center px-8 py-3.5 text-base font-semibold rounded-full text-white bg-primary hover:bg-primary-hover shadow-sm transition-colors"
                        >
                            {t("home.getInTouch")}
                        </Link>
                    </div>
                </div>
            </section>

            {/* Event Details / Poster Modal */}
            <EventModal
                event={selectedEvent}
                isOpen={Boolean(selectedEvent)}
                onClose={() => setSelectedEvent(null)}
                t={t}
            />
        </div>
    );
};

export default Home;

